/**
 * DebitCardPage — citizen virtual debit-card management.
 *
 * Live (service-financial-services, citizen endpoints):
 *   - POST /v1/cards/request          — request a virtual card (deterministic)
 *   - GET  /v1/cards/me               — list citizen's cards
 *   - POST /v1/cards/:id/freeze       — ACTIVE → FROZEN
 *   - POST /v1/cards/:id/unfreeze     — FROZEN → ACTIVE
 *   - POST /v1/cards/:id/cancel       — terminal CANCELLED
 *
 * Test-mode adapter: card last4 + PAN deterministic per customer; pan_hash
 * stored, cleartext PAN returned exactly once on request and never again.
 *
 * RBAC: Card/issue-own + Card/read-own + Card/freeze-own — granted to the
 * `customer` role by 20260509_nipost_pay_destub_perms.sql.
 */
import { useEffect, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Loader2,
  Lock,
  Trash2,
  Unlock,
} from 'lucide-react';

import {
  cancelVirtualCard,
  freezeVirtualCard,
  listMyVirtualCards,
  requestVirtualCard,
  unfreezeVirtualCard,
  type IssuedVirtualCard,
  type ListMyVirtualCardsResponse,
  type VirtualCard,
} from '../../lib/api/cards';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import {
  PageHeader,
  StatusBadge,
  formatNaira,
  useCustomerWallet,
} from './NipostPayUI';

function formatPan(pan: string): string {
  return pan.replace(/(.{4})/g, '$1 ').trim();
}

export function DebitCardPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, claims } = useCustomerAuth();
  const customerId = claims?.upmpUserId ?? '';
  const wallet = useCustomerWallet(customerId, token);

  const cardsQuery = useQuery<ListMyVirtualCardsResponse, Error>({
    queryKey: ['virtual-cards', customerId] as const,
    queryFn: () => listMyVirtualCards({ bearerToken: token ?? undefined }),
    enabled: customerId.length > 0 && token !== null,
    retry: false,
    staleTime: 30_000,
  });

  // The cleartext PAN is only returned by POST /request; we hold it in
  // page state for a one-time reveal and clear it on navigation.
  const [revealedPan, setRevealedPan] = useState<string | null>(null);

  const request = useMutation<IssuedVirtualCard, Error, undefined>({
    mutationFn: () => requestVirtualCard({ bearerToken: token ?? undefined }),
    onSuccess: (data) => {
      setRevealedPan(data.pan);
      queryClient.invalidateQueries({ queryKey: ['virtual-cards', customerId] });
    },
  });

  const freeze = useMutation<VirtualCard, Error, string>({
    mutationFn: (id) => freezeVirtualCard(id, { bearerToken: token ?? undefined }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['virtual-cards', customerId] }),
  });
  const unfreeze = useMutation<VirtualCard, Error, string>({
    mutationFn: (id) => unfreezeVirtualCard(id, { bearerToken: token ?? undefined }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['virtual-cards', customerId] }),
  });
  const cancel = useMutation<VirtualCard, Error, string>({
    mutationFn: (id) => cancelVirtualCard(id, { bearerToken: token ?? undefined }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['virtual-cards', customerId] }),
  });

  // Hide the revealed PAN after 60s for safety.
  useEffect(() => {
    if (!revealedPan) return;
    const timer = setTimeout(() => setRevealedPan(null), 60_000);
    return () => clearTimeout(timer);
  }, [revealedPan]);

  const cards = cardsQuery.data?.items ?? [];
  const activeCard = cards.find((c) => c.status === 'ACTIVE') ?? cards[0];

  return (
    <div>
      <PageHeader title="Debit card" description="Manage your NIPOST Pay virtual debit card." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Card visual + controls */}
        <div className="space-y-4">
          <div className="relative w-full aspect-[1.586/1] rounded-2xl bg-gradient-to-br from-nipost-green to-green-800 p-5 text-white shadow-xl overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-[-40px] right-[-40px] w-48 h-48 rounded-full bg-white" />
              <div className="absolute bottom-[-30px] left-[-30px] w-36 h-36 rounded-full bg-white" />
            </div>
            <div className="relative z-10 flex flex-col h-full justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-green-200">NIPOST Pay</p>
                  <p className="text-xs font-bold text-white mt-0.5">Virtual debit card</p>
                </div>
                <span
                  className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${
                    activeCard?.status === 'FROZEN' ? 'bg-red-500' : 'bg-green-400'
                  } text-white`}
                >
                  {activeCard?.status ?? 'NONE'}
                </span>
              </div>
              <div>
                <p
                  className="font-mono text-lg tracking-[0.15em] mb-2"
                  data-testid="card-pan-display"
                >
                  {revealedPan
                    ? formatPan(revealedPan)
                    : activeCard
                    ? `•••• •••• •••• ${activeCard.last4}`
                    : '•••• •••• •••• ----'}
                </p>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-[9px] text-green-300 uppercase">Wallet</p>
                    <p className="text-sm font-semibold">
                      {wallet.data
                        ? formatNaira(wallet.data.balanceCached ?? wallet.data.balance ?? '0')
                        : '—'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] text-green-300 uppercase">Expires</p>
                    <p className="text-sm font-semibold">
                      {activeCard
                        ? `${String(activeCard.expMonth).padStart(2, '0')}/${String(activeCard.expYear).slice(-2)}`
                        : '--/--'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] text-green-300 font-bold tracking-widest">
                      {activeCard?.brand ?? 'TEST'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {revealedPan ? (
            <div
              className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900"
              role="status"
            >
              <p className="font-semibold">Card details — display once only</p>
              <p className="mt-1">
                Save them now — for security we won't show the full number again. This banner clears in 60s.
              </p>
            </div>
          ) : null}

          {activeCard ? (
            <div className="bg-white rounded-xl border border-border p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Card controls</h3>
              <div className="grid grid-cols-2 gap-2">
                {activeCard.status === 'ACTIVE' ? (
                  <button
                    type="button"
                    onClick={() => freeze.mutate(activeCard.id)}
                    disabled={freeze.isPending}
                    className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-border hover:bg-gray-50 disabled:opacity-50"
                  >
                    {freeze.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                    Freeze
                  </button>
                ) : null}
                {activeCard.status === 'FROZEN' ? (
                  <button
                    type="button"
                    onClick={() => unfreeze.mutate(activeCard.id)}
                    disabled={unfreeze.isPending}
                    className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-nipost-green text-white border border-nipost-green disabled:opacity-50"
                  >
                    {unfreeze.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
                    Unfreeze
                  </button>
                ) : null}
                {activeCard.status !== 'CANCELLED' ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Cancel this card? This is irreversible.')) {
                        cancel.mutate(activeCard.id);
                      }
                    }}
                    disabled={cancel.isPending}
                    className="col-span-2 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    {cancel.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Cancel card
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        {/* Right column: request flow + history */}
        <div className="lg:col-span-2 space-y-4">
          {cards.every((c) => c.status === 'CANCELLED') || cards.length === 0 ? (
            <div className="bg-white rounded-xl border border-border p-5">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-nipost-green" /> Issue a virtual card
              </h3>
              <p className="text-sm text-muted mt-2">
                Generate a virtual debit card backed by your NIPOST Pay wallet. Use it online — freeze any time.
              </p>
              {request.isError ? (
                <div role="alert" className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                  {request.error.message}
                </div>
              ) : null}
              {request.isSuccess ? (
                <div
                  className="mt-3 rounded-lg bg-success-light border border-success/20 px-3 py-2 text-xs text-success flex items-center gap-2"
                  role="status"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" /> Card issued — last4 {request.data.last4}.
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => request.mutate(undefined)}
                disabled={request.isPending || !wallet.data}
                className="mt-4 w-full py-2.5 bg-nipost-green text-white font-semibold rounded-lg hover:bg-nipost-green-dark transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {request.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Request virtual card
              </button>
            </div>
          ) : null}

          {/* Past cards */}
          <div className="bg-white rounded-xl border border-border p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Card history</h3>
            {cardsQuery.isLoading ? (
              <p className="text-sm text-muted inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </p>
            ) : cards.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted text-left border-b border-border">
                    <tr>
                      <th className="py-2">Brand</th>
                      <th className="py-2">Last4</th>
                      <th className="py-2">Expires</th>
                      <th className="py-2">Status</th>
                      <th className="py-2">Issued</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cards.map((c) => (
                      <tr key={c.id} className="border-b border-gray-50 last:border-0">
                        <td className="py-2 font-medium">{c.brand}</td>
                        <td className="py-2 font-mono">•••• {c.last4}</td>
                        <td className="py-2">{String(c.expMonth).padStart(2, '0')}/{String(c.expYear).slice(-2)}</td>
                        <td className="py-2"><StatusBadge status={c.status} /></td>
                        <td className="py-2 text-muted">
                          {new Date(c.createdAt).toLocaleDateString('en-NG')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted">No cards yet.</p>
            )}
          </div>

          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted">
                Test-mode card numbers are deterministic per customer. Production swaps to a real card-issuer (Verve/MasterCard) via a one-line config change.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
